import { ForwardedRef, forwardRef, Fragment, useImperativeHandle, useRef } from 'react';
import { flexRender } from '@tanstack/react-table';
import { ReactTableDevtools } from '@tanstack/react-table-devtools';
import classNames from 'classnames';

import { DawnArrowDown, DawnArrowUp } from '@xemplo/icons';
import { CenteredLoaderDots } from '@xemplo/loader';
import { NoResult, NoResultSearchIcon } from '@xemplo/no-result';

import { ActionBar } from './action/action-bar';
import { TableBody, TableBodyTestId } from './body/body';
import { Pagination } from './pagination/pagination';
import { Totals } from './totals/totals';
import { hasCustomColumnSize, kebabCase } from './table.helper';
import * as S from './table.style';
import { TableProps, TableRef } from './table.types';
import { useTanstackTable } from './use-react-table';

export const TableTestId = {
  wrapper: 'table-wrapper',
  table: 'table',
  header: 'table-header',
  headerRow: 'table-header-row',
  body: TableBodyTestId,
};

/**
 * Tanstack Table allows boolean and function values to be passed for enableRowSelection.
 * So we are doing !!enableRowSelection to convert it to boolean.
 * It affects the data-selectable attribute on the table element.
 */

function XemploTableInternal<TData>(
  props: TableProps<TData>,
  ref?: ForwardedRef<TableRef<TData>>,
) {
  const { table, hasResults } = useTanstackTable(props);
  const {
    maxHeight,
    minHeight,
    minWidth: containerMinWidth, // TODO: Rename minWidth to containerMinWidth leaving for compatibility
    tableOverflowControlMinWidth,
    isListTable,
    enableTableDevTools,
    isLoading,
    noResultProps = {},
    pageSizeOptions,
    alwaysShowActionBar = false,
  } = props;
  const { enableExpanding, enableSorting, enableRowSelection, columns } = table.options;
  const theadRef = useRef<HTMLTableSectionElement>(null);
  const isExpandable =
    enableExpanding &&
    table.getCoreRowModel().rows.some((row) => row.subRows?.length > 0);

  useImperativeHandle(ref, () => ({ table }), [table]);

  const hasRows = table.getCoreRowModel().rows.length > 0;

  if (!hasResults && !isLoading) {
    return (
      <S.Container className="no-result" data-testid={TableTestId.wrapper}>
        <NoResult noResultImage={<NoResultSearchIcon />} {...noResultProps} />
      </S.Container>
    );
  }
  return (
    <Fragment>
      <S.Container
        data-testid={TableTestId.wrapper}
        className={`table-wrapper ${props.className}`}
        $minHeight={minHeight}
        $minWidth={containerMinWidth}
      >
        {hasRows && <ActionBar table={table} alwaysShowActionBar={alwaysShowActionBar} />}
        <S.TableOverflowControl>
          <S.Table
            data-testid={TableTestId.table}
            data-selectable={!!enableRowSelection}
            data-sortable={enableSorting}
            data-expandable={isExpandable}
            $maxHeight={maxHeight}
            $minWidth={tableOverflowControlMinWidth}
          >
            {hasRows && (
              <S.TableHeader
                data-testid={TableTestId.header}
                $minWidth={tableOverflowControlMinWidth}
                ref={theadRef}
              >
                {table.getHeaderGroups().map((headerGroup) => (
                  <tr key={headerGroup.id}>
                    {headerGroup.headers.map((header) => {
                      const isCheckboxCell = header.id === 'extra-col';

                      const headerCellClassName = classNames(
                        'no-user-select',
                        {
                          'extra-col-cell': isCheckboxCell,
                          'list-table': isListTable && isCheckboxCell && !isExpandable,
                        },
                        kebabCase(header.id),
                      );

                      return (
                        <S.HeaderCell
                          className={headerCellClassName}
                          key={header.id}
                          colSpan={header.colSpan}
                          $minWidth={header.column.columnDef.minSize}
                          $width={
                            hasCustomColumnSize(header.column.id, columns)
                              ? header.getSize()
                              : undefined
                          }
                          data-selectable={!!enableRowSelection}
                          data-expandable={isExpandable}
                        >
                          {header.isPlaceholder ? null : (
                            <S.CellWrapper
                              onClick={header.column.getToggleSortingHandler()}
                            >
                              {flexRender(
                                header.column.columnDef.header,
                                header.getContext(),
                              )}
                              {{
                                asc: <DawnArrowUp width={12} height={12} />,
                                desc: <DawnArrowDown width={12} height={12} />,
                              }[header.column.getIsSorted() as string] ?? null}
                            </S.CellWrapper>
                          )}
                        </S.HeaderCell>
                      );
                    })}
                  </tr>
                ))}
                <Totals table={table} />
              </S.TableHeader>
            )}
            <TableBody table={table} theadRef={theadRef} />
          </S.Table>
        </S.TableOverflowControl>
        <Pagination table={table} pageSizeOptions={pageSizeOptions} />
        {isLoading && <CenteredLoaderDots />}
      </S.Container>

      {enableTableDevTools && (
        <S.TableDevTools>
          <ReactTableDevtools table={table} panelProps={{ style: { width: '100vw' } }} />
        </S.TableDevTools>
      )}
    </Fragment>
  );
}

export const XemploTable = forwardRef(XemploTableInternal) as <TData>(
  props: TableProps<TData> & { ref?: ForwardedRef<TableRef<TData>> },
) => ReturnType<typeof XemploTableInternal>;

export default XemploTable;
